import TextButton from 'LuxKit/Button/TextButton'
import Badge from 'LuxKit/Badge'
import { ChertoExperiments } from 'constants/experiments'
import CookiesContext from 'contexts/CookiesContext'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { memo, useCallback, useContext } from 'react'
import { matchPath } from 'react-router-dom'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import useFlashOfferSignUpModal from 'components/OfferPage/FlashHotel/useFlashOfferSignUpModal'
import { hasPriorDeviceSession } from './hasPriorDeviceSession'

const LoginControl = styled.span`
  position: relative;
  display: inline-flex;
`

const LoginDot = styled.span`
  position: absolute;
  /* Centre the 8px dot on the pill corner. Medium buttons are 40px with a 20px radius. */
  top: ${rem(2)};
  inset-inline-end: ${rem(2)};
  pointer-events: none;
  line-height: 0;
  z-index: 1;
`

interface Props {
  className?: string
}

function NavBarLoginButton(props: Props) {
  const { className } = props

  const intl = useIntl()
  const showFlashOfferSignUpModal = useFlashOfferSignUpModal()
  const loggedIn = useAppSelector(selectLoggedIn)
  const pathname = useAppSelector(state => state.router.location.pathname)
  const offerId = matchPath({ path: '/:regionCode/offer/:slug/:offerId', end: true }, pathname)?.params.offerId
  const flashOffer = useAppSelector(state => {
    const offer = offerId ? state.offer.offers[offerId] : undefined
    return isFlashHotelOffer(offer) ? offer : undefined
  })
  const { getCookies } = useContext(CookiesContext)
  const { isAnyVariant } = useExperiment(ChertoExperiments.croRedDotOnLoggedOutUserProfile, {
    canActivate: !loggedIn,
  })
  // Request cookies on the server, document cookies in the browser. Same decision on both, so the label does not change after hydration.
  const hasPriorSession = hasPriorDeviceSession(getCookies())

  const onLoginClick = useCallback(() => {
    showFlashOfferSignUpModal(flashOffer, flashOffer?.lowestPricePackage)
  }, [flashOffer, showFlashOfferSignUpModal])

  const loginText = intl.formatMessage({
    id: 'PzcSUqTGzjiNHFXJC3x_',
    defaultMessage: 'Log in',
    description: 'Login button text in navigation bar on the login or signup page',
  })
  const signUpText = intl.formatMessage({
    id: 'u73Uw_ki3avQdLIxQRVc',
    defaultMessage: 'Sign up',
    description: 'Header button label for logged-out visitors with no previous session on this device',
  })

  const showSignupPrompt = isAnyVariant && !loggedIn
  const buttonText = showSignupPrompt && !hasPriorSession ? signUpText : loginText

  return (
    <LoginControl>
      <TextButton
        className={className}
        kind="secondary"
        variant="default"
        onClick={onLoginClick}
      >
        {buttonText}
      </TextButton>
      {showSignupPrompt && <LoginDot aria-hidden data-testid="nav-logged-out-signup-dot">
        <Badge kind="dot" mode="critical" />
      </LoginDot>}
    </LoginControl>
  )
}

export default memo(NavBarLoginButton)
